import { useEffect, useMemo, useState, useRef } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db, functions } from '../../../../../firebase-client';
import { safeOnSnapshot } from '../../../../../utils/firestoreSubscriptions';
import { ticketAppliesToEvent } from '../../../../../utils/ticketCoverage';

// Optional: enable verbose SDK logs globally in your app bootstrap
// import { setLogLevel } from 'firebase/app';
// setLogLevel('debug');

export function useTicketPurchase({
  teamId,
  eventId,
  userId,
  checkoutUrl,                 // optional pre-saved Stripe Checkout URL
  ticketName = 'General Admission',
  ticketPriceMajor = '',
  ticketCurrency = 'GBP',
  displayPriceMajor,
  ticketPaymentBasis = '',
  occurrenceId = null,
  isRecurring = false,
  forceCallableCheckout = false,
}) {
  const [busy, setBusy] = useState(false);
  const [paidCount, setPaidCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [refundedCount, setRefundedCount] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [loadError, setLoadError] = useState('');
  const inFlight = useRef(false);

  // Live subscribe to THIS USER'S tickets for this event
  useEffect(() => {
    if (!teamId || !eventId || !userId) return;
    const qy = query(
      collection(db, 'teams', teamId, 'events', eventId, 'tickets'),
      where('userId', '==', userId)
    );
    const unsub = safeOnSnapshot(qy, (snap) => {
      setLoadError('');
      let paid = 0;
      let pending = 0;
      let refunded = 0;
      const list = [];
      snap.forEach((docSnap) => {
        const ticket = docSnap.data();
        const applies = ticketAppliesToEvent(ticket, { isRecurring, ticketPaymentBasis, occurrenceId });
        if (!applies) return;
        const qty = Number(ticket?.quantity || 1);
        if (ticket?.status === 'paid' || ticket?.status === 'partially_refunded') paid += qty;
        if (ticket?.status === 'pending') pending += qty;
        if (ticket?.status === 'refunded') refunded += qty;
        list.push({ id: docSnap.id, ...ticket });
      });
      setTickets(list);
      setPaidCount(paid);
      setPendingCount(pending);
      setRefundedCount(refunded);
    }, () => {
      setLoadError('Your booking status could not be loaded. Refresh and try again.');
    }, { label: 'event-ticket-purchase', teamId });
    return unsub;
  }, [teamId, eventId, userId, isRecurring, occurrenceId, ticketPaymentBasis]);

  const status = useMemo(() => {
    if (paidCount > 0) return 'paid';
    if (pendingCount > 0) return 'pending';
    if (refundedCount > 0) return 'refunded';
    return 'none';
  }, [paidCount, pendingCount, refundedCount]);

  const isHttpUrl = (u) => {
    try {
      const x = new URL(u, window.location.origin);
      return x.protocol === 'https:' || x.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const alertFromCode = (code, message) => {
    const msg = (message || '').toLowerCase();
    if (code === 'unauthenticated') return alert('Please sign in to buy tickets.');
    if (code === 'invalid-argument') return alert('Something was missing. Please refresh and try again.');
    if (code === 'not-found' || /not\s+found/.test(msg)) return alert('Event not found.');
    if (code === 'failed-precondition') {
      if (/not on sale/.test(msg)) return alert('Tickets are not on sale right now.');
      if (/not ticketed/.test(msg)) return alert('This event is not ticketed.');
      if (/missing.*price|ticket price/.test(msg)) return alert('This event does not have a ticket price set yet.');
      if (/stripe.*configured|stripe.*key|finish stripe setup|stripe.*operational|stripe.*ready/.test(msg)) {
        return alert('Payments are temporarily unavailable. Please contact the organiser.');
      }
      return alert(`Cannot start checkout: ${message || 'precondition failed'}`);
    }
    if (code === 'resource-exhausted' || /capacity/.test(msg)) {
      return alert('Sorry—this event is at capacity.');
    }
    if (code === 'unavailable' || code === 'internal') {
      return null;
    }
    return alert(message ? `Failed to start checkout: ${message}` : 'Failed to start checkout.');
  };

  const startCheckout = async (quantity = 1, options = {}) => {
    if (!teamId || !eventId || !userId) {
      alert('Sorry—this event is not ready for checkout yet.');
      return false;
    }
    if (inFlight.current) {
      return false;
    }

    try {
      inFlight.current = true;
      setBusy(true);

      // Auth state
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        alert('Please sign in to buy tickets.');
        return false;
      }

      // If you’ve pre-saved a static checkout URL on the event, use it
      if (checkoutUrl && !isRecurring && !forceCallableCheckout) {
        if (!isHttpUrl(checkoutUrl)) {
          alert('Checkout link is invalid. Please contact the organiser.');
          return false;
        }
        window.location.assign(checkoutUrl);
        return true;
      }

      // Callable (only path)
      try {
        const callable = httpsCallable(functions, 'createEventCheckoutCallable');
        const payload = {
          teamId,
          eventId,
          quantity: Math.max(1, Number(quantity) || 1),
          ...(isRecurring && ticketPaymentBasis === 'per_occurrence' ? { occurrenceId } : {}),
          ...(options.waitlistEntryId ? { waitlistEntryId: options.waitlistEntryId } : {}),
          ...(options.ticketTypeId ? { ticketTypeId: options.ticketTypeId } : {}),
          ...(options.selectedExtras ? { selectedExtras: options.selectedExtras } : {}),
          ...(options.bookingAnswers ? { bookingAnswers: options.bookingAnswers } : {}),
          ...(options.attendeeAnswers ? { attendeeAnswers: options.attendeeAnswers } : {}),
          ...(options.saveProfileFields ? { saveProfileFields: true } : {}),
        };

        const { data } = await callable(payload);

        if (data?.completed) {
          setPaidCount((current) => Math.max(current, Math.max(1, Number(quantity) || 1)));
          return true;
        }

        const url = data && data.url;
        if (!isHttpUrl(url)) {
          alert('Checkout URL was invalid. Please try again.');
          return false;
        }
        window.location.assign(url);
        return true;
      } catch (err) {
        const code = (err && err.code) || '';
        const message = (err && err.message) || '';
        alertFromCode(code, message);
        return false;
      }
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  return {
    status,            // 'none' | 'pending' | 'paid'
    busy,
    paidCount,
    pendingCount,
    refundedCount,
    hasCompletedBooking: tickets.some(ticket => ['paid', 'partially_refunded', 'refunded'].includes(ticket.status)),
    startCheckout,
    tickets,
    loadError,
    ticketMeta: {
      name: ticketName,
      price: displayPriceMajor ?? ticketPriceMajor,
      currency: ticketCurrency,
  },
  };
}
